import { AppDispatch, RootState } from "src/store/store";
import {
  Box,
  Flex,
  useDisclosure,
} from "@chakra-ui/react";
import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AdminService } from "src/rest/adminService";
import DashboardPage from "./dashboard/DashboardPage";
import { DetailModal } from "./detailModal";
import { GlobalUtils } from "src/utils/globalUtils";
import { StatusCode } from "src/common/constants/constantEnums";
import { UseAppToast } from "src/common/useAppToast";
import { UserDetails } from "src/types/userDetails";
import { setProfileLoading } from "src/store/features/admin";
import { useNavigate } from "react-router-dom";
import {
  STATUS_APPROVED,
  STATUS_REJECTED
} from "src/common/constants/statusConstants";

export default function Admin() {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const dispatch = useDispatch<AppDispatch>();
  const { successToast, errorToast } = UseAppToast();
  const [userDetailsList, setUserDetailsList] = useState<UserDetails[]>([]);
  const [checkList, setCheckList] = useState(new Array<String>());

  const [user, setCurrentUser] = useState<UserDetails | null>(null);

  const [checkedItems, setCheckedItems] = useState(
    new Array(userDetailsList.length).fill(false)
  );

  const [isAllChecked, setIsAllChecked] = useState(false);

  const globalInfo = useSelector((state: RootState) => state.globalInfo);
  const { globalLanguage } = globalInfo;

  useEffect(() => {
    GlobalUtils.setGlobalLanguage(dispatch);
  }, [dispatch]);

  useEffect(() => {
    getUserApplications();
  }, []);

  const navigate = useNavigate();
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      navigate("/", { replace: true });
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  const getUserApplications = async (): Promise<void> => {
    try {
      const response = await AdminService.getUserApplications();
      if (response.status === StatusCode.SUCCESS_STATUS_CODE) {
        setUserDetailsList(response.data);
      }
    } catch (error) {
      errorToast("Error while fetching user applications");
    }
  };

  const approveUserApplication = async (
    users: UserDetails[],
    action: typeof STATUS_APPROVED | typeof STATUS_REJECTED,
    reason?: string
  ) => {
    // Filter out users without emailId
    const validUsers = users.filter(u => u.emailId);
    if (!validUsers.length) return errorToast("User email not found!");

    // Always send array of emails
    const emailIds = validUsers.map(u => u.emailId).join(",");
    dispatch(setProfileLoading(true));
    try {
      const response = await AdminService.approveUserApplication(emailIds, action, reason);

      if (response.status === StatusCode.SUCCESS_STATUS_CODE) {
        updateUserDetails(validUsers);
        showMessage(validUsers, action);
      }
    } catch (error: any) {
      console.error("Approve error:", error.response || error);
      errorToast(`Failed to ${action.toLowerCase()} application`);
    } finally {
      dispatch(setProfileLoading(false));
    }
  };

  const updateUserDetails = (users: UserDetails[]) => {
    const updatedList: UserDetails[] = userDetailsList?.filter(user => !users?.some(ele => ele?.emailId === user?.emailId));
    const updatedCheckList: String[] = checkList?.filter(user => !users?.some(ele => ele?.communicationDetails?.mobileNumber === user));
    const tempCheckedItems: Boolean[] = [];
    checkedItems.forEach((data) => {
      if (data !== true) {
        tempCheckedItems.push(data);
      }
    });
    setCheckedItems(tempCheckedItems);
    setUserDetailsList(updatedList);
    setCheckList(updatedCheckList);
  }

  const showMessage = (users: UserDetails[], action: string) => {
    if (users?.length === 1) {
      if (action === STATUS_APPROVED) {
        successToast(`${users[0]?.personalDetails?.firstName}'s application approved successfully`);
      } else {
        errorToast(`${users[0]?.personalDetails?.firstName}'s application is rejected`);
      }
    } else {
      if (action === STATUS_APPROVED) {
        successToast("All selected applications are approved");
      } else {
        errorToast("All selected applications are rejected");
      }
    }
  }

  // Pending approvals handlers (scoped for dashboard props)
  /*const onClickApproveAll = async () => {
    const updatedList: UserDetails[] = [];
    userDetailsList.forEach((ele) => {
      if (checkList.includes(ele?.communicationDetails?.mobileNumber)) {
        updatedList.push(ele);
      }
    });
    await approveUserApplication(updatedList, "Approved");
  };*/

  const onMasterCheckboxChange = (e: { target: { checked: boolean } }) => {
    const checked = e.target.checked;
    setIsAllChecked(checked);
    setCheckedItems(new Array(userDetailsList.length).fill(checked));

    if (checked) {
      const tempList: String[] = [];
      userDetailsList.forEach((data) => {
        tempList.push(data?.communicationDetails?.mobileNumber);
      });
      setCheckList(tempList);
    } else {
      setCheckList([]);
    }
  };

  const onRowCheckboxChange = (
    e: ChangeEvent<HTMLInputElement>,
    index: number,
    data: string | String
  ) => {
    const checked = e.target.checked;
    const updatedCheckedItems = [...checkedItems];
    updatedCheckedItems[index] = checked;
    setCheckedItems(updatedCheckedItems);

    if (checked) {
      setCheckList((prev) => [...prev, data]);
    } else {
      setCheckList((prev) => prev.filter((ele) => ele !== data));
    }
    setIsAllChecked(updatedCheckedItems.every((item) => item));
  };

  // Ensure dashboard pending approvals props are defined
  const headers = ["Name", "Reason For Joining", "Mobile", "DOB", "Action"];
  const hindiHeaders = [
    // match Admin_Labels_Hindi if available
    "नाम",
    "शामिल होने का कारण",
    "मोबाइल",
    "जन्म तिथि",
    "कार्य",
  ];

  const approveAllBackgroundColor = checkList.length !== 0 ? "#E35E00" : "#E2E2E2";
  const rejectAllBackgroundColor = checkList.length !== 0 ? "white" : "#E2E2E2";
  const rejectAllFontColor = checkList.length !== 0 ? "#E35E00" : "white";
  const rejectAllBorderColor = checkList.length !== 0 ? "2px solid #E35E00" : "2px solid #E2E2E2";

  return (
    <Flex direction="column" height="100vh" width="100%" px={0} bg="#F8F7F5">
      {/* Admin Dashboard Area */}
      <Box flex="1" mb={0} minH={0}>
        <DashboardPage
          headerOffset={0}
          pendingProps={{
            userDetailsList,
            isHindiLabels: globalLanguage === "HI",
            headers,
            hindiHeaders,
            approveAllBackgroundColor,
            rejectAllBackgroundColor,
            rejectAllFontColor,
            rejectAllBorderColor,
            checkList,
            isAllChecked,
            handleMasterCheckboxChange: onMasterCheckboxChange,
            checkedItems: checkedItems as any,
            handleRowCheckboxChange: onRowCheckboxChange,
            onRowClick: (u) => { setCurrentUser(u); onOpen(); },
            approveUserApplication,
          }}
        />
      </Box>

      {/* Pending approvals moved into Dashboard; keep modal here */}
      <DetailModal
       user={user}
       isOpen={isOpen}
       onClose={onClose}
       approveUserApplication={approveUserApplication}
      />

    </Flex>
  );
}